/* useTurnstile — Cloudflare Turnstile bot-challenge helper.
 *
 * Cloudflare deprecated `size: "invisible"`. Invisible mode is now a
 * dashboard-level *widget mode* (Managed | Non-Interactive | Invisible).
 * Whatever mode the operator picked in the dashboard, our job in the
 * browser is just to: (a) load the api.js, (b) render a widget into a
 * container we control, (c) pass `appearance: "interaction-only"` so
 * legitimate users never see a UI, and (d) read the resulting token.
 *
 * Usage:
 *   const { mount, getToken, configured } = useTurnstile();
 *   <div ref={mount} />                   // render the widget container
 *   const token = await getToken();       // returns "" when not configured,
 *                                         // a token string on success,
 *                                         // null on failure.
 */
import { useCallback, useEffect, useRef, useState } from "react";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
const SCRIPT_ID = "cf-turnstile-api";

export default function useTurnstile() {
  const [ready, setReady] = useState(!SITE_KEY); // when not configured, "ready" == "skip"
  const widgetIdRef = useRef(null);
  const containerRef = useRef(null);
  const lastTokenRef = useRef("");

  // Lazy-load Cloudflare's api.js once per page.
  useEffect(() => {
    if (!SITE_KEY) return;
    let s = document.getElementById(SCRIPT_ID);
    if (!s) {
      s = document.createElement("script");
      s.id = SCRIPT_ID;
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    }
    const onReady = () => { if (window.turnstile) setReady(true); };
    if (window.turnstile) onReady();
    else s.addEventListener("load", onReady, { once: true });
    return () => s.removeEventListener("load", onReady);
  }, []);

  // Ref callback — passed as `ref={mount}` on the widget container <div>.
  const mount = useCallback((node) => {
    containerRef.current = node || null;
  }, []);

  // Render the widget into the mounted container as soon as both are ready.
  useEffect(() => {
    if (!SITE_KEY || !ready || !containerRef.current || widgetIdRef.current) return;
    try {
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        appearance: "interaction-only", // hidden unless CF needs a challenge
        retry: "auto",
        callback: (token) => { lastTokenRef.current = token || ""; },
        "error-callback":   () => { lastTokenRef.current = ""; },
        "timeout-callback": () => { lastTokenRef.current = ""; },
        "expired-callback": () => { lastTokenRef.current = ""; },
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("turnstile render failed:", e);
    }
  }, [ready]);

  // Resolve the current token. If the widget hasn't auto-completed yet,
  // poll briefly (CF usually issues one within a couple hundred ms).
  const getToken = useCallback(async () => {
    if (!SITE_KEY) return ""; // not configured — backend will accept
    if (!ready || !window.turnstile) return null;
    if (lastTokenRef.current) return lastTokenRef.current;

    // Poll up to ~6s for an auto-issued token.
    for (let i = 0; i < 30; i++) {
      try {
        const t = widgetIdRef.current ? window.turnstile.getResponse(widgetIdRef.current) : "";
        if (t) { lastTokenRef.current = t; return t; }
      } catch { /* widget not yet rendered */ }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 200));
    }
    return null;
  }, [ready]);

  return { mount, getToken, ready, configured: !!SITE_KEY };
}
